import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import {
  selectIsLoadingGroups,
  selectIsLoadingZones,
  setIsInitialized,
  addAlert,
  fetchCurrentUser,
  selectAvailableZones,
  fetchZones,
  fetchGroups,
} from 'store/global'
import { usePermissions } from 'hooks/usePermissions'
import {
  selectDisplayedGroup,
  setDisplayedGroup,
  closeDisplayedGroup,
  deleteGroup,
  editGroup,
  createGroup,
} from 'store/organizations'
import messages from './messages'
import { selectAllGroups } from 'store/global'
import GroupModal from 'components/GroupModal/GroupModal'
import Loader from 'components/Loader/Loader'
import { orderBy } from 'lodash'
import styles from './Groups.module.scss'
import { saveAs } from 'file-saver'
import { Parser } from 'json2csv'
import Search from 'UI/Search/Search'
import { Table, Empty, Button } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'

export default function Groups() {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const isLoading = useSelector(selectIsLoadingGroups)
  const isLoadingZones = useSelector(selectIsLoadingZones)
  const displayedGroup = useSelector(selectDisplayedGroup)
  const allGroups = useSelector(selectAllGroups)
  const availableZones = useSelector(selectAvailableZones) || []
  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    dispatch(closeDisplayedGroup())
    dispatch(setIsInitialized(true))
    dispatch(fetchZones())
    dispatch(fetchGroups())
  }, [])

  const onSort = column => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const flattenGroups = groups => {
    let flattened = []

    groups.forEach(group => {
      const groupWithLevel = {
        ...group,
        displayName: group.path,
        canEdit: group.canEdit || false,
        canDelete: group.canDelete || false,
      }
      flattened.push(groupWithLevel)

      if (group.subGroups && group.subGroups.length > 0) {
        flattened = flattened.concat(flattenGroups(group.subGroups))
      }
    })
    return flattened
  }

  const flattenedGroups = React.useMemo(() => {
    if (!allGroups) return []
    const flattened = flattenGroups(allGroups)
    return flattened
  }, [allGroups])

  const filteredGroups = React.useMemo(() => {
    if (!searchTerm) return flattenedGroups

    const searchLower = searchTerm.toLowerCase()
    return flattenedGroups.filter(
      group => group.name.toLowerCase().includes(searchLower) || group.displayName.toLowerCase().includes(searchLower),
    )
  }, [flattenedGroups, searchTerm])

  const sortedGroups = React.useMemo(() => {
    if (!sortColumn) return filteredGroups
    return orderBy(filteredGroups, [sortColumn], [sortOrder])
  }, [filteredGroups, sortColumn, sortOrder])

  const onDisplayModal = (action = 'create', existingGroup = {}) => {
    const newGroup = {
      ...existingGroup,
      action,
      isOpen: true,
    }

    dispatch(setDisplayedGroup({ group: newGroup }))
  }

  const onSubmit = async group => {
    if (displayedGroup.action === 'delete') {
      dispatch(deleteGroup({ groupId: group.id }))
    } else if (displayedGroup.action === 'create') {
      dispatch(createGroup({ group }))
    } else {
      dispatch(editGroup({ group }))
    }

    dispatch(addAlert({ type: 'success', message: 'Group Action Performed' }))
    dispatch(setDisplayedGroup())
    dispatch(fetchCurrentUser())
  }

  const onExportCSV = () => {
    if (!sortedGroups.length) return
    const exportData = sortedGroups.map(group => {
      const zoneNames = (group.zoneIds || [])
        .map(zoneId => {
          const zone = availableZones.find(z => z.id === zoneId)
          return zone ? zone.name : zoneId
        })
        .join(', ')

      return {
        name: group.displayName,
        zones: zoneNames,
      }
    })

    const fields = ['name', 'zones']
    const parser = new Parser({ fields })
    const csv = parser.parse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'groups.csv')
  }

  const tableColumns = [
    {
      title: <FormattedMessage {...messages.columns.name} />,
      dataIndex: 'displayName',
      key: 'name',
      width: 200,
      sorter: true,
      sortOrder: sortColumn === 'name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('name'),
        style: { cursor: 'pointer' },
      }),
      ellipsis: true,
    },
    {
      title: <FormattedMessage {...messages.columns.zones} />,
      key: 'zones',
      width: 300,
      render: (_, record) => {
        const zoneNames = (record.zoneIds || [])
          .map(zoneId => {
            const zone = availableZones.find(z => z.id === zoneId)
            return zone ? zone.name : zoneId
          })
          .join(', ')

        return (
          <div style={{ 
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            maxWidth: '280px'
          }}>
            {zoneNames || ''}
          </div>
        )
      },
    },
    {
      title: <FormattedMessage {...messages.columns.actions} />,
      key: 'actions',
      width: 100,
      render: (_, record) => {
        return (
          <div className={styles.actions}>
            <div onClick={() => onDisplayModal('delete', record)} className={styles.action}>
              <DeleteOutlined />
            </div>
            <div onClick={() => onDisplayModal('edit', record)} className={styles.action}>
              <EditOutlined />
            </div>
          </div>
        )
      },
    },
  ]

  if (isLoading || isLoadingZones) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <Loader type='globalLoading' message='Loading groups and zones...' />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerActions}>
          {permissions.admin && (
            <Button onClick={() => onDisplayModal('create')}>
              <PlusOutlined /> New Group
            </Button>
          )}
        <div className={styles.headerActionsRight}>
          <Search
            placeholder='Search for...'
            enterButton
            allowClear
            style={{ width: 264 }}
            onSearch={value => setSearchTerm(value)}
          />
          <Button onClick={onExportCSV} disabled={!sortedGroups.length} type='primary'>
            <UploadOutlined /> Export CSV
          </Button>
        </div>
      </div>

      <Table
        columns={tableColumns}
        dataSource={sortedGroups}
        loading={isLoading}
        rowKey='id'
        scroll={{ x: 600, y: 550 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
        }}
        locale={{
          emptyText: <Empty description='No groups found' />,
        }}
      />

      {displayedGroup.isOpen && <GroupModal mode={displayedGroup.action} onSubmit={onSubmit} />}
    </div>
  )
}
